/**
 * API: Municipality Score Alpha (Sprint 13 — Wow 1).
 *
 * Score precalculado + breakdown sectorial + action plan + "show your
 * work" del modal de transparencia.
 */

import { request } from "./_client";

export interface SectorialScoreDTO {
  sector: string;
  score: number | null;
  captacion_cop: string; // Decimal serializado
  peer_avg_cop: string;
}

export interface HistoricalSectorDTO {
  sector: string;
  pct: number;
  total_captacion_cop: string;
}

export interface MunicipalityScoreDTO {
  dane_code: string;
  has_score: boolean;
  degraded_reason: string | null;

  score_alpha: number | null;
  percentile: number | null;
  peer_group_size: number;
  peer_group_definition: string;
  rank_in_peer_group: number | null;

  captacion_total_cop: string;
  project_count_total: number;
  peer_avg_captacion_cop: string;
  peer_best_captacion_cop: string;
  peer_best_dane: string | null;
  /** Nombre del mejor del grupo (resuelto desde peer_best_dane).
   *  NULL si el DANE no resuelve. */
  peer_best_name: string | null;
  peer_best_department: string | null;

  sectorial_scores: SectorialScoreDTO[];
  historical_top_sectors: HistoricalSectorDTO[];

  last_recalculated_at: string;
  data_as_of: string;

  executive_summary: string;
}

export interface ScoreProjectDTO {
  bpin: string;
  name: string;
  sector: string;
  amount_approved_cop: string | null;
  status: string;
}

export interface PeerGroupMemberDTO {
  dane_code: string;
  name: string;
  department_name: string;
}

export interface ScoreDetailsDTO {
  dane_code: string;
  formula_text: string;
  status_filter_included: string[];
  peer_group: PeerGroupMemberDTO[];
  muni_projects: ScoreProjectDTO[];
  disclaimer: string;
}

/** Score Alpha precalculado del municipio. */
export function fetchMunicipalityScore(dane: string): Promise<MunicipalityScoreDTO> {
  return request<MunicipalityScoreDTO>(`/municipalities/${encodeURIComponent(dane)}/score`);
}

// ----- Next Actions Briefing (Sprint Pre-Demo Alcaldesa) -------------
//
// Reemplazó al /action-plan en la Ficha Municipal por una narrativa con
// 3 bloques + 1 CTA. El cliente y el endpoint de /action-plan se
// retiraron en el issue #755: nadie los usaba y su estado "perfecto"
// celebraba justo cuando nada aplicaba al municipio.

export interface ActiveOpportunitiesBlockDTO {
  count: number;
  next_deadline_days: number | null;
  href: string;
}

export interface NeighborMovementBlockDTO {
  neighbor_name: string;
  /** Decimal serializado por Pydantic ("81000000000"). */
  amount_cop: string;
  sector: string;
  vigencia: number | null;
  sector_alignment: "strong" | "weak" | "neutral";
  muni_sector_score: number | null;
  /** False si es un par socioeconómico de otro departamento, no un vecino. */
  is_geographic_neighbor: boolean;
}

export interface WeakSectorBlockDTO {
  sector: string;
  score: number;
  active_opportunities_count: number;
  href: string;
}

export interface NextActionsBriefingDTO {
  dane_code: string;
  briefing_week_label: string;
  has_content: boolean;
  active_opportunities: ActiveOpportunitiesBlockDTO | null;
  neighbor_movement: NeighborMovementBlockDTO | null;
  weak_sector: WeakSectorBlockDTO | null;
  cta_label: string;
  cta_href: string;
}

/**
 * Briefing consolidado de Próximas Acciones.
 *
 * Sprint Blindaje — SEC-1 (julio 2026): el cliente ya no envía
 * `tenant_id` — el backend lo deriva del JWT vía `ensure_context`.
 * Enviarlo en el query string exponía el tenant en la clear (logs de
 * acceso, historial del navegador, proxies) sin necesidad, ya que el
 * dato ya viaja autenticado en el Bearer.
 */
export function fetchNextActionsBriefing(dane: string): Promise<NextActionsBriefingDTO> {
  return request<NextActionsBriefingDTO>(
    `/municipalities/${encodeURIComponent(dane)}/next-actions-briefing`,
  );
}

/** Show your work — lazy load al abrir el modal. */
export function fetchMunicipalityScoreDetails(dane: string): Promise<ScoreDetailsDTO> {
  return request<ScoreDetailsDTO>(`/municipalities/${encodeURIComponent(dane)}/score-details`);
}

/** Proyecto SGR historico para drill-down de la Ficha Municipal. */
export interface SgrHistoricalProjectDTO {
  bpin: string;
  name: string;
  sector: string;
  /** Monto aprobado en COP, como string (Decimal del backend) */
  amount_approved_cop: string;
  status: string | null;
  biennial_period: string | null;
  /** ISO date string (YYYY-MM-DD) o null */
  first_disbursed_at: string | null;
}

/** Obtener proyectos SGR historicos de un muni para drill-down. */
export function fetchMunicipalitySgrProjects(dane: string): Promise<SgrHistoricalProjectDTO[]> {
  return request<SgrHistoricalProjectDTO[]>(
    `/municipalities/${encodeURIComponent(dane)}/sgr-projects`,
  );
}
